import { useCallback, useMemo, useState } from 'react';
import {
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { usePageTitle } from '../../components/shell/AppShell';
import { RouteDataGrid } from '../../components/data-grid/RouteDataGrid';
import type { QuickFilter, RowAction, SearchField } from '../../components/data-grid/RouteDataGrid';
import { EntityDetailDialog, auditTrailFields } from '../../components/dialogs/EntityDetailDialog';
import type { DetailRow } from '../../components/dialogs/EntityDetailDialog';
import { ConfirmDialog } from '../../components/dialogs/ConfirmDialog';
import { fmtDateTime, fmtOrEmDash } from '../../utils/format';
import type { ExportColumn } from '../../utils/export';
import type { Operator } from '../../api/types';
import {
  useMyPermissions,
  useOperatorAction,
  useOperatorsList,
} from '../../api/queries';
import { OperatorFormDialog } from './OperatorFormDialog';

export default function OperatorsPage() {
  usePageTitle('Operators');
  const { queryKey, listFn } = useOperatorsList();
  const perms = useMyPermissions();
  const has = useCallback(
    (code: string) => perms.data?.codes?.includes(code) ?? false,
    [perms.data?.codes],
  );

  const [detailRow, setDetailRow] = useState<Operator | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editRow, setEditRow] = useState<Operator | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<{
    op: Operator; action: 'suspend' | 'restore';
  } | null>(null);
  const opAction = useOperatorAction(confirmTarget?.op.id ?? '00000000-0000-0000-0000-000000000000');

  const columns = useMemo<GridColDef<Operator>[]>(() => [
    { field: 'displayName', headerName: 'Operator', flex: 1.5,
      renderCell: (p) => (
        <Stack sx={{ py: 0.5 }}>
          <Typography variant="body2">{p.row.displayName}</Typography>
          <Typography variant="caption" color="text.secondary">{p.row.legalName}</Typography>
        </Stack>
      ) },
    { field: 'code', headerName: 'Code', width: 140,
      renderCell: (p) => (
        <Typography component="code" sx={{
          fontFamily: 'monospace', fontSize: 12,
          bgcolor: 'action.hover', px: 0.75, py: 0.25, borderRadius: 0.5,
        }}>{p.row.code}</Typography>
      ) },
    { field: 'country', headerName: 'Country', width: 110 },
    { field: 'status', headerName: 'Status', width: 140,
      renderCell: (p) => <StatusChip status={p.row.status} /> },
    { field: 'createdAt', headerName: 'Created', width: 180,
      valueFormatter: (value) => fmtDateTime(value) },
  ], []);

  const searchFields = useMemo<SearchField[]>(() => [
    { kind: 'text',   key: 'displayName', label: 'Operator name' },
    { kind: 'text',   key: 'code',        label: 'Code' },
    { kind: 'text',   key: 'country',     label: 'Country' },
    { kind: 'select', key: 'status',      label: 'Status', options: [
      { value: 'active',       label: 'Active' },
      { value: 'provisioning', label: 'Provisioning' },
      { value: 'suspended',    label: 'Suspended' },
      { value: 'disabled',     label: 'Disabled' },
    ] },
  ], []);

  const quickFilters = useMemo<QuickFilter<Operator>[]>(() => [
    { label: 'All',         filter: () => true },
    { label: 'Active',      filter: (r) => r.status === 'active' },
    { label: 'Provisioning', filter: (r) => r.status === 'provisioning' },
    { label: 'Suspended',   filter: (r) => r.status === 'suspended' },
  ], []);

  const exportColumns = useMemo<ExportColumn<Operator>[]>(() => [
    { key: 'code',          label: 'Code' },
    { key: 'displayName',   label: 'Display Name' },
    { key: 'legalName',     label: 'Legal Name' },
    { key: 'country',       label: 'Country' },
    { key: 'stateProvince', label: 'State / Province', format: (v) => fmtOrEmDash(v) },
    { key: 'timeZone',      label: 'Time Zone' },
    { key: 'status',        label: 'Status' },
    { key: 'dedicated',     label: 'Dedicated', format: (v) => (v ? 'Yes' : 'No') },
    { key: 'createdAt',     label: 'Created',  format: (v) => fmtDateTime(v) },
    { key: 'updatedAt',     label: 'Updated',  format: (v) => fmtDateTime(v) },
    { key: 'disabledAt',    label: 'Disabled', format: (v) => fmtDateTime(v) },
  ], []);

  const actions = useMemo<RowAction<Operator>[]>(() => [
    { label: 'View', onClick: (row) => setDetailRow(row) },
    {
      label: 'Edit',
      visible: () => has('platform.operators.create'),
      onClick: (row) => setEditRow(row),
    },
    {
      label: 'Suspend',
      visible: (row) => has('platform.operators.suspend') && row.status === 'active',
      onClick: (row) => setConfirmTarget({ op: row, action: 'suspend' }),
    },
    {
      label: 'Restore',
      visible: (row) => has('platform.operators.suspend') && row.status === 'suspended',
      onClick: (row) => setConfirmTarget({ op: row, action: 'restore' }),
    },
  ], [has]);

  const detailFields = useMemo<DetailRow<Operator>[]>(() => [
    { label: 'Operator',      render: (r) => r.displayName },
    { label: 'Legal Name',    render: (r) => r.legalName },
    { label: 'Code',          render: (r) => <code>{r.code}</code> },
    { label: 'Country',       render: (r) => r.country },
    { label: 'State / Prov.', render: (r) => fmtOrEmDash(r.stateProvince) },
    { label: 'Time Zone',     render: (r) => r.timeZone },
    { label: 'Status',        render: (r) => <StatusChip status={r.status} /> },
    { label: 'Dedicated',     render: (r) => (r.dedicated ? 'Yes' : 'No') },
    { label: 'Disabled At',   render: (r) => fmtDateTime(r.disabledAt) },
    ...auditTrailFields<Operator>(),
  ], []);

  return (
    <>
      <Stack spacing={2}>
        <Typography variant="h4">Operators</Typography>

        <RouteDataGrid<Operator>
          entityName="Operators"
          queryKey={queryKey}
          listFn={listFn}
          columns={columns}
          searchFields={searchFields}
          quickFilters={quickFilters}
          exportColumns={exportColumns}
          reportTitle="ARMS — Operators"
          actions={actions}
          primaryAction={
            has('platform.operators.create')
              ? { label: '+ Create operator', onClick: () => setCreateOpen(true) }
              : undefined
          }
          onRowClick={(row) => setDetailRow(row)}
          emptyMessage="No operators yet."
        />

        <EntityDetailDialog<Operator>
          open={detailRow !== null}
          title={detailRow ? `${detailRow.displayName}` : 'Operator'}
          data={detailRow}
          fields={detailFields}
          onClose={() => setDetailRow(null)}
        />

        <OperatorFormDialog
          open={createOpen}
          mode="create"
          onClose={() => setCreateOpen(false)}
        />

        <OperatorFormDialog
          open={editRow !== null}
          mode="edit"
          operator={editRow ?? undefined}
          onClose={() => setEditRow(null)}
        />

        <ConfirmDialog
          open={confirmTarget !== null}
          title={confirmTarget?.action === 'suspend' ? 'Suspend operator' : 'Restore operator'}
          message={confirmTarget ? (
            confirmTarget.action === 'suspend'
              ? <>Suspend operator <strong>{confirmTarget.op.displayName}</strong>? Their users won't be able to sign in until restored.</>
              : <>Restore operator <strong>{confirmTarget.op.displayName}</strong>? Users can sign in again.</>
          ) : ''}
          confirmLabel={confirmTarget?.action === 'suspend' ? 'Suspend' : 'Restore'}
          destructive={confirmTarget?.action === 'suspend'}
          busy={opAction.isPending}
          error={(opAction.error as { message?: string } | null)?.message}
          onConfirm={() => {
            if (!confirmTarget) return;
            opAction.mutate(confirmTarget.action, {
              onSuccess: () => setConfirmTarget(null),
            });
          }}
          onCancel={() => setConfirmTarget(null)}
        />
      </Stack>
    </>
  );
}

function StatusChip({ status }: { status: string }) {
  const color = status === 'active'    ? 'success'
              : status === 'suspended' ? 'error'
              : status === 'provisioning' ? 'warning'
              : 'default';
  return <Chip size="small" label={status} color={color as 'success' | 'error' | 'warning' | 'default'} />;
}
